import type { Answer, TraceMessage } from "../../types";
import { TraceAnswerElements } from "../TraceAnswerElements/TraceAnswerElements";
import { buildPiiHighlightedSegments } from "../../utils/buildPiiHighlightedSegments";
import styles from "./TraceConversation.module.css";

export interface TraceConversationProps {
  messages: TraceMessage[];
  selectedAssistantMessageIndex: number;
  answersByMessageIndex?: Partial<Record<number, Answer>>;
}

function formatTimestamp(timestamp?: string | null): string | null {
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return date.toLocaleString();
}

function MessageText({ message }: { message: TraceMessage }) {
  const segments = buildPiiHighlightedSegments(
    message.text,
    message.pii_highlights,
  );

  return (
    <p className={styles.messageText}>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          return <span key={index}>{segment.content}</span>;
        }

        return (
          <mark
            key={index}
            className={styles.piiHighlight}
            title={`PII pattern: ${segment.label}`}
          >
            {segment.content}
            <span className={styles.piiLabel}>{segment.label}</span>
          </mark>
        );
      })}
    </p>
  );
}

function TraceMessageBody({
  message,
  messageIndex,
  answersByMessageIndex,
}: {
  message: TraceMessage;
  messageIndex: number;
  answersByMessageIndex?: Partial<Record<number, Answer>>;
}) {
  const answer = answersByMessageIndex?.[messageIndex];
  if (message.role === "assistant" && answer) {
    return <TraceAnswerElements answer={answer} />;
  }

  return <MessageText message={message} />;
}

export function TraceConversation({
  messages,
  selectedAssistantMessageIndex,
  answersByMessageIndex,
}: TraceConversationProps) {
  if (!messages.length) {
    return (
      <section className={styles.container} aria-label="Conversation trace">
        <h2 className={styles.heading}>Conversation</h2>
        <p className={styles.empty}>No messages in this trace.</p>
      </section>
    );
  }

  return (
    <section className={styles.container} aria-label="Conversation trace">
      <h2 className={styles.heading}>Conversation</h2>
      <ol className={styles.messageList}>
        {messages.map((message, index) => {
          const isSelected = index === selectedAssistantMessageIndex;
          const timestamp = formatTimestamp(message.timestamp);

          return (
            <li
              key={index}
              className={`${styles.message} ${isSelected ? styles.messageSelected : ""}`}
              data-testid={`trace-message-${index}`}
              data-selected={isSelected ? "true" : "false"}
            >
              <div className={styles.messageHeader}>
                <span
                  className={styles.roleBadge}
                  data-role={message.role}
                >
                  {message.role}
                </span>
                {isSelected && (
                  <span className={styles.selectedBadge}>Feedback target</span>
                )}
                {message.role === "assistant" &&
                  message.response_kind && (
                    <span className={styles.responseKind}>
                      {message.response_kind}
                    </span>
                  )}
                {timestamp && (
                  <time className={styles.timestamp} dateTime={message.timestamp ?? undefined}>
                    {timestamp}
                  </time>
                )}
              </div>
              <TraceMessageBody
                message={message}
                messageIndex={index}
                answersByMessageIndex={answersByMessageIndex}
              />
            </li>
          );
        })}
      </ol>
    </section>
  );
}
